/**
 * Types and constants for Schedule Lines Tab components.
 *
 * Mirrors the PO Lines types.ts pattern with column config,
 * filter state, and status option definitions.
 */
import type { POLineSchedule } from '../../../../lib/api/types/purchasing';

// Column configuration for visibility management
export interface ScheduleColumnConfig {
  id: string;
  label: string;
  visible: boolean;
  sortable: boolean;
  minWidth: string;
  order: number;
}

export const DEFAULT_SCHEDULE_COLUMNS: ScheduleColumnConfig[] = [
  { id: 'acknowledge_status', label: 'Ack Status', visible: true, sortable: true, minWidth: '150px', order: 0 },
  { id: 'action', label: 'Action', visible: true, sortable: false, minWidth: '110px', order: 1 },
  { id: 'on_hold', label: 'On Hold', visible: true, sortable: true, minWidth: '90px', order: 2 },
  { id: 'waiting_on', label: 'Waiting On', visible: true, sortable: true, minWidth: '110px', order: 3 },
  { id: 'po_number', label: 'PO Number', visible: true, sortable: true, minWidth: '130px', order: 4 },
  { id: 'line_number', label: 'Line #', visible: true, sortable: true, minWidth: '60px', order: 5 },
  { id: 'schedule_line_number', label: 'Sched Line', visible: true, sortable: true, minWidth: '90px', order: 6 },
  { id: 'part_number', label: 'Part Number', visible: true, sortable: true, minWidth: '150px', order: 7 },
  { id: 'description', label: 'Description', visible: false, sortable: true, minWidth: '200px', order: 8 },
  { id: 'supplier_name', label: 'Supplier', visible: true, sortable: true, minWidth: '180px', order: 9 },
  { id: 'warehouse', label: 'Warehouse', visible: true, sortable: false, minWidth: '120px', order: 10 },
  { id: 'customer_name', label: 'Company', visible: false, sortable: true, minWidth: '150px', order: 11 },
  { id: 'schedule_date', label: 'Schedule Date', visible: true, sortable: true, minWidth: '130px', order: 12 },
  { id: 'supplier_promise_date', label: 'Promise Date', visible: true, sortable: true, minWidth: '130px', order: 13 },
  { id: 'original_promise_date', label: 'Original Promise', visible: false, sortable: true, minWidth: '140px', order: 14 },
  { id: 'schedule_qty', label: 'Sched Qty', visible: true, sortable: true, minWidth: '100px', order: 15 },
  { id: 'shipped_qty', label: 'Shipped', visible: true, sortable: true, minWidth: '90px', order: 16 },
  { id: 'balance_qty', label: 'Balance', visible: true, sortable: true, minWidth: '90px', order: 17 },
  { id: 'unit_of_measure', label: 'UOM', visible: false, sortable: true, minWidth: '70px', order: 18 },
  { id: 'unit_price', label: 'Unit Price', visible: false, sortable: true, minWidth: '110px', order: 19 },
  { id: 'status', label: 'Status', visible: true, sortable: true, minWidth: '130px', order: 20 },
  { id: 'updated_at', label: 'Updated', visible: false, sortable: true, minWidth: '140px', order: 21 },
];

export const SCHEDULE_COLUMN_STORAGE_KEY = 'schedule_lines_column_config';

// Filter state interface
export interface ScheduleFilters {
  poNumber: string;
  lineNumber: string;
  scheduleLineNumber: string;
  partNumber: string;
  supplier: string;
  warehouse: string;
  acknowledgeStatus: string;
  scheduleStatus: string;
  onHold: string;
  scheduleDateFrom: string;
  scheduleDateTo: string;
  promiseDateFrom: string;
  promiseDateTo: string;
}

export const DEFAULT_SCHEDULE_FILTERS: ScheduleFilters = {
  poNumber: '',
  lineNumber: '',
  scheduleLineNumber: '',
  partNumber: '',
  supplier: '',
  warehouse: '',
  acknowledgeStatus: '',
  scheduleStatus: '',
  onHold: '',
  scheduleDateFrom: '',
  scheduleDateTo: '',
  promiseDateFrom: '',
  promiseDateTo: '',
};

// Warehouse filter options (display names mapped backend-side from plant codes).
export const WAREHOUSE_OPTIONS = [
  { value: 'Warwick', label: 'Warwick' },
  { value: 'Blackpool', label: 'Blackpool' },
];

export const ACKNOWLEDGE_STATUS_OPTIONS = [
  { value: 'new', label: 'New' },
  { value: 'accepted', label: 'Accepted' },
  { value: 'declined', label: 'Declined' },
  { value: 'change_proposed_supplier', label: 'Change Proposed (Supplier)' },
  { value: 'change_proposed_customer', label: 'Change Proposed (Customer)' },
  { value: 'change_declined', label: 'Change Declined' },
  { value: 'wip', label: 'WIP' },
  { value: 'despatched', label: 'Despatched' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'closed', label: 'Closed' },
  // Pseudo-status: rows whose delivery date falls outside the firm
  // window. Backed by is_forecast filter rather than acknowledge_status.
  { value: 'forecast', label: 'Forecast' },
];

export const SCHEDULE_STATUS_OPTIONS = [
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'partially_shipped', label: 'Partially Shipped' },
  { value: 'shipped', label: 'Shipped' },
  { value: 'cancelled', label: 'Cancelled' },
];

// Acknowledge status color mapping (matches PO Lines pattern)
export const ACK_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  new: { label: 'New', className: 'bg-yellow-100 text-yellow-800' },
  accepted: { label: 'Accepted', className: 'bg-green-100 text-green-800' },
  declined: { label: 'Declined', className: 'bg-red-100 text-red-800' },
  change_declined: { label: 'Change Declined', className: 'bg-red-100 text-red-800' },
  change_proposed_supplier: { label: 'Changed (Supplier)', className: 'bg-blue-100 text-blue-800' },
  change_proposed_customer: { label: 'Changed (Customer)', className: 'bg-blue-100 text-blue-800' },
  wip: { label: 'WIP', className: 'bg-purple-100 text-purple-800' },
  despatched: { label: 'Despatched', className: 'bg-teal-100 text-teal-800' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800' },
  queried: { label: 'Queried', className: 'bg-orange-100 text-orange-800' },
  closed: { label: 'Closed', className: 'bg-gray-100 text-gray-500' },
};

// Schedule status color mapping
export const SCHEDULE_STATUS_CONFIG: Record<string, { label: string; className: string }> = {
  scheduled: { label: 'Scheduled', className: 'bg-blue-100 text-blue-800' },
  partially_shipped: { label: 'Partially Shipped', className: 'bg-orange-100 text-orange-800' },
  shipped: { label: 'Shipped', className: 'bg-green-100 text-green-800' },
  cancelled: { label: 'Cancelled', className: 'bg-red-100 text-red-800' },
};

/** Tab values for schedule lines, matching PO Lines parity. */
export type ScheduleTab = 'all' | 'needs_action' | 'due_this_week' | 'due_next_week' | 'open' | 'closed' | 'on_hold' | 'forecasted';

// Re-export for convenience
export type { POLineSchedule };
